import { Subscription } from "@aika/types";
import logoSrc from "./img/logo.svg";
import logoDark from "./img/logoDark.svg";
import logoSrcFounder from "./img/logoFounder.svg";
import logoDarkFounder from "./img/logoDarkFounder.svg";
import { DefaultTheme } from "styled-components";

const baseline = 6;
const baseFontSize = 16;
const borderRadius = 5;

const calcLineHeight = (size: number) =>
  ((Math.ceil(size / baseline) + 1) * baseline) / size;

// Major third typography
const calcFontsize = (base: number, order: number) =>
  base * Math.pow(1.25, order);

const breakpoints = ["768px", "1024px", "1400px", "1920px"];

// Aliases
const breakpointNames = ["sm", "md", "lg", "xl"];
breakpointNames.forEach((x, i) => {
  breakpoints[x] = breakpoints[i];
});

const colors = {
  background: "#ffffff",
  black: "#111111",
  body: "#5A5D6F",
  bg: "#7f8c8d",
  bgLight: "#466375",
  primary: "#27ae60",
  grey0: "#f0f6f7",
  grey1: "#E7E9EA",
  grey2: "#dfe6e9",
  grey3: "#b2bec3",
  grey4: "#636e72",
  grey5: "#2d3436",
  danger: "#A53F2B",
  positive: "#71BC44",
  warning: "#eb9500",
};

const base = {
  baseline,
  baseLineHeight: calcLineHeight(baseFontSize),
  baseFontSize,
  borderRadius,
  breakpoints,
  borders: ["0", `1px solid ${colors.grey3}`, `2px solid ${colors.grey5}`],
  shadows: [
    "none",
    "0 2px 5px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.2)",
    "0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)",
    "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
  ],
  fonts: {
    body: "Lato,Helvetica,sans-serif",
  },
  fontSizes: [-2, -1.5, -1, 0, 1, 2, 3, 4]
    .map((x) => calcFontsize(baseFontSize, x))
    .map(Math.round),
  colors,
  blockColors: [
    "#55efc4",
    "#81ecec",
    "#74b9ff",
    "#a29bfe",
    "#ffeaa7",
    "#fab1a0",
    "#ff7675",
    "#fd79a8",
  ],
  space: [0, 6, 12, 18, 24, 36, 48, 72, 96],
};

export type AikaTheme = {
  id: string;
  name: string;
  dark: boolean;
  colors: {
    primary?: string;
    background?: string;
    body?: string;
    grey0?: string;
    grey1?: string;
    grey2?: string;
    grey3?: string;
    grey4?: string;
    grey5?: string;
  };
  blockColors: string[];
};

export const themes: AikaTheme[] = [
  {
    id: "aika",
    name: "Aika",
    dark: false,
    colors: {
      primary: "#27ae60",
      background: "white",
    },
    blockColors: [
      "#48dbb2",
      "#69e0e0",
      "#74b9ff",
      "#a29bfe",
      "#ffda79",
      "#ffb570",
      "#ff7675",
      "#ff9ec1",
      "#91E4CD",
      "#B3E0E0",
      "#BCDBFB",
      "#D8D5FF",
      "#FFECBA",
      "#FFD9B6",
      "#FFB2B1",
      "#FFDAE7",
    ],
  },
  {
    id: "ocean",
    name: "Ocean",
    dark: false,
    colors: {
      primary: "#8FA1B3",
      background: "#EFF1F5",
    },
    blockColors: [
      "#364462",
      "#bf616a",
      "#a3be8c",
      "#ebcb8b",
      "#8fa1b3",
      "#b48ead",
      "#96b5b4",
      "#c0c5ce",
      "#636975",
      "#C49EA2",
      "#C3D1B8",
      "#E9DABC",
      "#ADB6BF",
      "#C8B4C4",
      "#BACCCB",
      "#DDE1E8",
    ],
  },
  {
    id: "springsky",
    name: "Spring Sky",
    dark: false,
    colors: {
      primary: "#b0d6eb",
      background: "white",
    },
    blockColors: [
      "#EFEFEF",
      "#F4E9E3",
      "#F6ECF4",
      "#E8F4F2",
      "#D8ECFF",
      "#FFFCDB",
      "#F4E9E3",
      "#E9F6FE",
      "#E3DCDC",
      "#FDEEDB",
      "#F4E0F0",
      "#E8FDEA",
      "#E8F3F9",
      "#F7F8D9",
      "#E9D2C5",
      "#FFF1E5",
    ],
  },
  {
    id: "leafshade",
    dark: true,
    name: "Leafshade",
    colors: {
      primary: "#5f819d",
      body: "#dcdccc",
      background: "#282a2e",
      grey5: "#f0f6f7",
      grey4: "#E7E9EA",
      grey3: "#dfe6e9",
      grey2: "#b2bec3",
      grey1: "#636e72",
      grey0: "#2d3436",
    },
    blockColors: [
      "#a54242",
      "#8c9440",
      "#de935f",
      "#5f819d",
      "#85678f",
      "#5e8d87",
      "#707880",
      "#f0c674",
      "#772D2D",
      "#4F5423",
      "#995729",
      "#325674",
      "#573B61",
      "#1F443F",
      "#353D46",
      "#AF8636",
    ],
  },
  {
    id: "ashes",
    name: "Ashes",
    dark: true,
    colors: {
      background: "#1C2023",
      body: "#C7CCD1",
      primary: "#95AEC7",
      grey5: "#f0f6f7",
      grey4: "#E7E9EA",
      grey3: "#dfe6e9",
      grey2: "#b2bec3",
      grey1: "#636e72",
      grey0: "#2d3436",
    },
    blockColors: [
      "#6d7ac7",
      "#c7ae95",
      "#95c7ae",
      "#aec795",
      "#ae95c7",
      "#c795ae",
      "#95aec7",
      "#d18181",
      "#BBC3EF",
      "#E1D3C5",
      "#C9D8D0",
      "#E1EED4",
      "#D3C4E3",
      "#D0B7C3",
      "#989CA0",
      "#D09C9C",
    ],
  },
  {
    id: "twilight",
    name: "Twilight",
    dark: true,
    colors: {
      background: "#1E1E1E",
      body: "#A7A7A7",
      primary: "#CF6A4C",
      grey5: "#f0f6f7",
      grey4: "#E7E9EA",
      grey3: "#dfe6e9",
      grey2: "#b2bec3",
      grey1: "#43484a",
      grey0: "#2d3436",
    },
    blockColors: [
      "#3c6d59",
      "#cf6a4c",
      "#8f9d6a",
      "#f9ee98",
      "#7587a6",
      "#9b859d",
      "#afc4db",
      "#a75c5c",
      "#29473B",
      "#803D29",
      "#5D6646",
      "#BEB571",
      "#546076",
      "#8495A8",
      "#484E54",
      "#724141",
    ],
  },
  {
    id: "monokai",
    name: "Monokai",
    dark: true,
    colors: {
      background: "#272822",
      body: "#F8F8F2",
      primary: "#AE81FF",
      grey5: "#f0f6f7",
      grey4: "#E7E9EA",
      grey3: "#dfe6e9",
      grey2: "#b2bec3",
      grey1: "#43484a",
      grey0: "#2d3436",
    },
    blockColors: [
      "#f92672",
      "#a6e22e",
      "#f4bf75",
      "#66d9ef",
      "#ae81ff",
      "#a1efe4",
      "#ff5652",
      "#89BDFF",
      "#EB6696",
      "#8CB737",
      "#D09C54",
      "#9AD6E2",
      "#C0A9E8",
      "#D7E9E6",
      "#F18B88",
      "#6D91BE",
    ],
  },
  {
    id: "falcon",
    name: "Falcon",
    dark: true,
    colors: {
      primary: "#48DBB2",
      body: "#dcdccc",
      background: "#282a2e",
      grey5: "#f0f6f7",
      grey4: "#E7E9EA",
      grey3: "#dfe6e9",
      grey2: "#b2bec3",
      grey1: "#636e72",
      grey0: "#2d3436",
    },
    blockColors: [
      "#48dbb2",
      "#69e0e0",
      "#74b9ff",
      "#a29bfe",
      "#ffda79",
      "#ffb570",
      "#ff7675",
      "#ff9ec1",
      "#91E4CD",
      "#B3E0E0",
      "#BCDBFB",
      "#D8D5FF",
      "#FFECBA",
      "#FFD9B6",
      "#FFB2B1",
      "#FFDAE7",
    ],
  },
  {
    id: "cyberpunk",
    name: "Cyberpunk",
    dark: true,
    colors: {
      primary: "#1C9697",
      body: "#dcdccc",
      background: "#282a2e",
      grey5: "#f0f6f7",
      grey4: "#E7E9EA",
      grey3: "#dfe6e9",
      grey2: "#b2bec3",
      grey1: "#636e72",
      grey0: "#2d3436",
    },
    blockColors: [
      "#02404D",
      "#2E4D45",
      "#282960",
      "#442968",
      "#8F2E99",
      "#EA267A",
      "#E28825",
      "#FFCC00",
      "#2FFEE1",
      "#2E9975",
      "#3D3FC0",
      "#6C1CD7",
      "#E57ED5",
      "#DD4F89",
      "#AAAAF2",
      "#F2BCAA",
    ],
  },
];

function getTheme(
  settings: { theme: string },
  subscription: Subscription
): DefaultTheme {
  const isFounder = subscription != null && subscription.type === "founder";
  const theme = themes.find((x) => settings.theme === x.id) || themes[0];

  let lightLogo = isFounder ? logoSrcFounder : logoSrc;
  let darkLogo = isFounder ? logoDarkFounder : logoDark;

  return {
    ...base,
    logo: theme.dark ? darkLogo : lightLogo,
    dark: theme.dark,
    colors: {
      ...base.colors,
      ...theme.colors,
    },
    blockColors: theme.blockColors,
  };
}

export default getTheme;
